import { reportBustlyUserLanguage } from '@client-app/lib/bustly-gateway';
import { useTranslation } from '@client-app/i18n';
import { useReusableSkillSuggestionsPreference } from '@client-app/lib/reusable-skill-suggestions-preference';
import { Switch } from '@client-app/components/ui/Switch';
import { CaretDown, Check } from '@phosphor-icons/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import React, { useState } from 'react';

const GeneralSettings: React.FC = () => {
    const { locale, setLocale, t } = useTranslation();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [showReusableSkillSuggestions, setShowReusableSkillSuggestions] =
        useReusableSkillSuggestionsPreference();

    const languages = [
        { code: 'en', label: 'English (United States)' },
        { code: 'zh-CN', label: '简体中文' }
    ] as const;

    const currentLanguage = languages.find(l => l.code === locale) || languages[0];

    const handleLanguageChange = (code: 'en' | 'zh-CN') => {
        setLocale(code);
        void reportBustlyUserLanguage({
            language: code,
            source: 'client-locale-change',
        }).catch(() => {});
        setIsDropdownOpen(false);
    };

    return (
        <div className="space-y-6">
            <div className="rounded-xl border border-[#E8EBF3] bg-[#FCFCFC] p-6 shadow-[0_12px_30px_rgba(26,22,47,0.06)]">
                <div className="space-y-6">
                    {/* Language Setting Row */}
                    <div className="flex items-center justify-between py-1">
                        <div className="space-y-1">
                            <label className="block text-sm font-medium text-[#1A162F]">
                                {t('website.settingsModal.language')}
                            </label>
                            <p className="text-xs text-[#666F8D]">
                                {t('website.settingsModal.languageDescription')}
                            </p>
                        </div>

                        <div className="relative">
                            <DropdownMenu.Root
                                open={isDropdownOpen}
                                onOpenChange={setIsDropdownOpen}
                            >
                                <DropdownMenu.Trigger asChild>
                                    <div
                                        id="general-language-dropdown-trigger"
                                        className={clsx(
                                            'flex w-[220px] items-center justify-between rounded-[10px] border px-4 py-2.5 transition-all outline-none',
                                            'cursor-pointer border-[#E8EBF3] bg-white hover:border-[#C8D0E2]',
                                            isDropdownOpen && 'border-[#1A162F] ring-2 ring-[#1A162F]/20'
                                        )}
                                    >
                                        <span className="text-sm font-normal text-[#1A162F]">
                                            {currentLanguage.label}
                                        </span>
                                        <CaretDown
                                            size={16}
                                            weight="bold"
                                            className={clsx(
                                                'text-[#8A93B2] transition-transform duration-200',
                                                isDropdownOpen && 'rotate-180'
                                            )}
                                        />
                                    </div>
                                </DropdownMenu.Trigger>

                                <DropdownMenu.Portal>
                                <DropdownMenu.Content
                                        className="z-[10002] origin-top overflow-hidden rounded-xl border border-[#E8EBF3] bg-white shadow-[0_16px_40px_rgba(26,22,47,0.12)] animate-in fade-in zoom-in-95 duration-100"
                                        sideOffset={6}
                                        align="end"
                                        style={{ width: 'var(--radix-dropdown-menu-trigger-width)', maxWidth: 'calc(100vw - 16px)' }}
                                    >
                                        <div className="space-y-0.5 p-1.5">
                                            {languages.map((lang) => {
                                                const isSelected = locale === lang.code;
                                                return (
                                                    <DropdownMenu.Item
                                                        key={lang.code}
                                                        id={`language-option-${lang.code}`}
                                                        onSelect={() => handleLanguageChange(lang.code)}
                                                        className={clsx(
                                                            'flex cursor-pointer items-center justify-between rounded-[10px] px-4 py-2.5 text-sm outline-none transition-colors',
                                                            isSelected
                                                                ? 'bg-[#F4F5F8] text-[#1A162F]'
                                                                : 'text-[#1A162F] hover:bg-[#F8F9FC]'
                                                        )}
                                                    >
                                                        <span>{lang.label}</span>
                                                        {isSelected ? (
                                                            <Check size={16} weight="bold" className="text-[#1A162F]" />
                                                        ) : null}
                                                    </DropdownMenu.Item>
                                                );
                                            })}
                                        </div>
                                </DropdownMenu.Content>
                                </DropdownMenu.Portal>
                            </DropdownMenu.Root>
                        </div>
                    </div>

                    <div className="flex items-center justify-between border-t border-[#E8EBF3] pt-6">
                        <div className="max-w-[520px] space-y-1 pr-6">
                            <label className="block text-sm font-medium text-[#1A162F]">
                                {t('website.settingsModal.reusableSkillSuggestions')}
                            </label>
                            <p className="text-xs leading-5 text-[#666F8D]">
                                {t('website.settingsModal.reusableSkillSuggestionsDescription')}
                            </p>
                        </div>
                        <Switch
                            checked={showReusableSkillSuggestions}
                            label={t('website.settingsModal.reusableSkillSuggestions')}
                            onCheckedChange={setShowReusableSkillSuggestions}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GeneralSettings;
